/**
 * Pretty-print JSON by re-indenting its TEXT, never by parse → stringify.
 *
 * `JSON.stringify(JSON.parse(s), null, 2)` goes through JS numbers, so every
 * integer above 2^53 is rounded (`9007199254740993` prints as `…992`) and a
 * value the server sent is shown as one it did not send. This walks the
 * characters instead: strings and number/literal tokens are copied exactly as
 * written; only whitespace between tokens changes. For ordinary JSON the
 * output is identical to `JSON.stringify(parsed, null, 2)`.
 *
 * Throws (via JSON.parse) on invalid JSON, like the function it replaces —
 * callers fall back to the raw text on a throw.
 */
// A number token (after `:`, `[`, `,` or at the start) with 16+ digits, or an
// exponent. Either can print differently after a round trip through a JS
// number. A digit run inside a string may match too; that only costs speed.
const UNSAFE_NUMBER = /(?:^|[:[,])\s*-?(?:(?:\d\.?){16}|\d[\d.]*[eE])/;

/** True when `JSON.stringify(JSON.parse(text))` could change a number literal. */
export function hasUnsafeJsonNumber(text) {
  return UNSAFE_NUMBER.test(String(text));
}

export function reindentJson(text, indentUnit = '  ') {
  const src = String(text);
  JSON.parse(src); // validity gate only; the parsed value is never printed

  const out = [];
  let depth = 0;
  const newline = () => out.push('\n', indentUnit.repeat(depth));
  const isWs = (c) => c === ' ' || c === '\n' || c === '\r' || c === '\t';

  for (let i = 0; i < src.length; i += 1) {
    const c = src[i];
    if (isWs(c)) continue;
    if (c === '"') {
      let j = i + 1;
      while (j < src.length && src[j] !== '"') j += src[j] === '\\' ? 2 : 1;
      out.push(src.slice(i, j + 1));
      i = j;
    } else if (c === '{' || c === '[') {
      const close = c === '{' ? '}' : ']';
      let k = i + 1;
      while (k < src.length && isWs(src[k])) k += 1;
      if (src[k] === close) {
        out.push(c, close); // `{}` / `[]`, as JSON.stringify prints them
        i = k;
      } else {
        out.push(c);
        depth += 1;
        newline();
      }
    } else if (c === '}' || c === ']') {
      depth -= 1;
      newline();
      out.push(c);
    } else if (c === ',') {
      out.push(',');
      newline();
    } else if (c === ':') {
      out.push(': ');
    } else {
      // number / true / false / null — the whole token, verbatim
      let j = i + 1;
      while (j < src.length && !isWs(src[j]) && !',:]}'.includes(src[j])) j += 1;
      out.push(src.slice(i, j));
      i = j - 1;
    }
  }
  return out.join('');
}

/**
 * The other direction: JSON with every whitespace character OUTSIDE strings
 * removed — strings, numbers and literals copied exactly as written, so
 * `compactJson(reindentJson(s)) === s` for any single-line JSON without spaces.
 * The DB Manager's Edit cell shows JSON pretty-printed and saves an edit back
 * in this form, so the display never changes how the value is stored (owner,
 * 06.10.2026). Throws (via JSON.parse) on invalid JSON.
 */
export function compactJson(text) {
  const src = String(text);
  JSON.parse(src); // validity gate only
  const out = [];
  for (let i = 0; i < src.length; i += 1) {
    const c = src[i];
    if (c === ' ' || c === '\n' || c === '\r' || c === '\t') continue;
    if (c === '"') {
      let j = i + 1;
      while (j < src.length && src[j] !== '"') j += src[j] === '\\' ? 2 : 1;
      out.push(src.slice(i, j + 1));
      i = j;
    } else {
      out.push(c);
    }
  }
  return out.join('');
}
